import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { promptLogin } from "@/lib/authRedirect";
import { toast } from "sonner";
import { ChevronLeft, Loader2, BookmarkX } from "lucide-react";
import SEO from "@/components/site/SEO";
import DiscussionCard from "@/components/site/DiscussionCard";

/**
 * MyBookmarks — /me/bookmarks
 * Lists every discussion the current user has bookmarked (newest bookmark first).
 * Unbookmarking a card removes it from the list on next render (via onBookmarkChanged callback).
 */
export default function MyBookmarks() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { promptLogin(nav); return; }
    (async () => {
      try {
        const r = await api.get("/me/bookmarks/discussions");
        setItems(r.data || []);
      } catch {
        toast.error("Gagal memuat bookmark Anda.");
      } finally { setLoading(false); }
    })();
  }, [user, authLoading, nav]);

  const onBookmarkChanged = (discussion_id, isBookmarked) => {
    if (!isBookmarked) {
      // User un-bookmarked from within this list — drop the card so it disappears.
      setItems((prev) => prev.filter((d) => d.discussion_id !== discussion_id));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-alabaster">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-alabaster" data-testid="bookmarks-page">
      <SEO title="Bookmark saya — The School Reports" description="Diskusi yang Anda simpan untuk dibaca ulang." />

      <div className="max-w-2xl mx-auto px-5 pt-8 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => nav(-1)} aria-label="Kembali" data-testid="bookmarks-back" className="w-9 h-9 rounded-full bg-white border border-brand-border hover:border-brand-primary/40 flex items-center justify-center">
            <ChevronLeft className="w-5 h-5 text-brand-text" />
          </button>
          <div>
            <p className="eyebrow text-brand-accent">DISKUSI TERSIMPAN</p>
            <h1 className="font-serif-display text-2xl md:text-3xl text-brand-primary leading-tight">Bookmark Anda</h1>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-brand-border p-10 text-center shadow-sm" data-testid="bookmarks-empty">
            <BookmarkX className="w-8 h-8 text-[#8B8B90] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#4A4A4F] mb-4">Anda belum menyimpan diskusi apa pun.</p>
            <Link to="/forum" className="text-brand-accent font-semibold text-sm hover:underline">
              Kembali ke beranda →
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((d) => (
              <DiscussionCard key={d.discussion_id} d={d} onBookmarkChanged={onBookmarkChanged} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
